import type { Release } from "./types";

function isImmediatePlayerRelease(release: Release): boolean {
  return release.playback?.native === "on-the-fly";
}

function normalizedQuality(release: Release): string {
  return String(release.quality ?? "")
    .trim()
    .toLowerCase();
}

const UNSPECIFIED_AUDIO_CODES = new Set(["und", "unk", "unknown"]);

/** Language codes suitable for viewer-facing choices. */
export function releaseAudioLanguages(release: Release): string[] {
  return [
    ...new Set(
      (release.audioLanguages ?? [])
        .map((code) => code.trim().toLowerCase())
        .filter((code) => code && !UNSPECIFIED_AUDIO_CODES.has(code)),
    ),
  ].sort();
}

function qualityHeight(release: Release): number {
  const quality = normalizedQuality(release);
  if (quality === "2160p" || quality === "4k" || quality === "uhd") return 2160;
  return Number(quality.match(/^(\d{3,4})[pi]$/)?.[1] ?? 0);
}

function optionKey(release: Release): string {
  return [
    normalizedQuality(release) || "unknown",
    release.hdr ? "hdr" : "sdr",
    releaseAudioLanguages(release).join(",") || "original-audio",
  ].join("|");
}

function isBetterOption(candidate: Release, current: Release): boolean {
  if (
    isImmediatePlayerRelease(candidate) !== isImmediatePlayerRelease(current)
  ) {
    return isImmediatePlayerRelease(candidate);
  }
  const seederDifference =
    Number(candidate.seeders ?? 0) - Number(current.seeders ?? 0);
  if (seederDifference !== 0) return seederDifference > 0;
  return Number(candidate.score ?? 0) > Number(current.score ?? 0);
}

/** One reliable source per viewer-facing quality and audio combination. */
export function releaseOptions(releases: Release[]): Release[] {
  const options = new Map<string, Release>();
  for (const release of releases) {
    const key = optionKey(release);
    const current = options.get(key);
    if (!current || isBetterOption(release, current)) options.set(key, release);
  }

  return [...options.values()].sort((left, right) => {
    const immediateDifference =
      Number(isImmediatePlayerRelease(right)) -
      Number(isImmediatePlayerRelease(left));
    if (immediateDifference !== 0) return immediateDifference;
    const qualityDifference = qualityHeight(right) - qualityHeight(left);
    if (qualityDifference !== 0) return qualityDifference;
    return releaseAudioLanguages(left)
      .join(",")
      .localeCompare(releaseAudioLanguages(right).join(","));
  });
}
